import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import { formatUkDate } from "../../utils/dateUtils";

export interface PageVisitStats {
  totalPageViews: number;
  uniqueVisitorSessions: number;
  topPages: { path: string; views: number; uniqueSessions: number }[];
  pageViewsByDay: { date: string; views: number; uniqueSessions: number }[];
  pageEngagement: {
    path: string;
    avgDurationSec: number;
    avgScrollDepth: number;
    samples: number;
  }[];
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat().format(value);
}

function formatPercent(part: number, whole: number): string {
  if (whole <= 0) return "0%";
  return `${((part / whole) * 100).toFixed(1)}%`;
}

function formatDuration(seconds: number): string {
  if (seconds <= 0) return "—";
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}

function truncatePath(path: string, max = 52): string {
  if (path.length <= max) return path;
  return `…${path.slice(-(max - 1))}`;
}

function StatCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: string | number;
  sub?: string;
}) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03] md:p-6">
      <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
      <p className="mt-1 text-2xl font-bold tabular-nums text-gray-800 dark:text-white/90">
        {typeof value === "number" ? formatNumber(value) : value}
      </p>
      {sub ? (
        <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">{sub}</p>
      ) : null}
    </div>
  );
}

function Panel({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03] md:p-6">
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-gray-800 dark:text-white/90">
          {title}
        </h3>
        {subtitle ? (
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {subtitle}
          </p>
        ) : null}
      </div>
      {children}
    </div>
  );
}

function Skeleton() {
  return (
    <div className="animate-pulse rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] md:p-6">
      <div className="h-4 w-24 rounded bg-gray-200 dark:bg-gray-800" />
      <div className="mt-3 h-8 w-16 rounded bg-gray-200 dark:bg-gray-800" />
    </div>
  );
}

const chartBase: ApexOptions = {
  chart: {
    fontFamily: "Outfit, sans-serif",
    toolbar: { show: false },
    animations: { enabled: true, speed: 450 },
  },
  grid: {
    borderColor: "#e5e7eb",
    strokeDashArray: 3,
    padding: { left: 8, right: 12 },
  },
};

interface Props {
  stats: PageVisitStats | null;
  loading: boolean;
}

export default function PageVisitAnalytics({ stats, loading }: Props) {
  if (loading) {
    return (
      <div className="mb-6 space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-800 dark:text-white/90">
            Page visits
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Every page viewed on the public site (includes browse-only traffic)
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-6">
          <Skeleton />
          <Skeleton />
        </div>
      </div>
    );
  }

  if (!stats) return null;

  const dayLabels = stats.pageViewsByDay.map((d) =>
    formatUkDate(d.date, { weekday: "short", month: "short", day: "numeric" }),
  );

  const trendOptions: ApexOptions = {
    ...chartBase,
    chart: { ...chartBase.chart, type: "line", zoom: { enabled: false } },
    stroke: { curve: "smooth", width: [3, 3] },
    colors: ["#465fff", "#12b76a"],
    markers: {
      size: 4,
      strokeWidth: 2,
      strokeColors: ["#fff", "#fff"],
      hover: { size: 6 },
    },
    xaxis: {
      categories: dayLabels,
      labels: { style: { fontSize: "11px", colors: "#667085" }, rotate: -25 },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        style: { fontSize: "11px", colors: "#98a2b3" },
        formatter: (v) => formatNumber(Math.round(v)),
      },
      min: 0,
    },
    legend: {
      position: "top",
      horizontalAlign: "right",
      fontSize: "12px",
      markers: { size: 6, shape: "circle" },
    },
    tooltip: {
      shared: true,
      intersect: false,
      theme: "light",
      y: { formatter: (v) => formatNumber(Number(v)) },
    },
  };

  return (
    <div className="mb-8 space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-800 dark:text-white/90">
          Page visits
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Every page viewed on the public site (includes browse-only traffic)
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-6">
        <StatCard
          label="Total page views"
          value={stats.totalPageViews}
          sub="All navigations tracked"
        />
        <StatCard
          label="Unique sessions"
          value={stats.uniqueVisitorSessions}
          sub={
            stats.totalPageViews > 0
              ? `${(stats.totalPageViews / Math.max(stats.uniqueVisitorSessions, 1)).toFixed(1)} pages / session avg`
              : undefined
          }
        />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Panel
          title="Page views over time"
          subtitle="Daily totals — all pages combined"
        >
          {stats.pageViewsByDay.length > 0 ? (
            <Chart
              type="line"
              height={280}
              options={trendOptions}
              series={[
                {
                  name: "Page views",
                  data: stats.pageViewsByDay.map((d) => d.views),
                },
                {
                  name: "Unique sessions",
                  data: stats.pageViewsByDay.map((d) => d.uniqueSessions),
                },
              ]}
            />
          ) : (
            <p className="text-sm text-gray-500">No page view data yet</p>
          )}
        </Panel>

        <Panel
          title="Top pages"
          subtitle="Most viewed paths in the selected period"
        >
          {stats.topPages.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-xs">
                <thead className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500 dark:bg-gray-900/60 dark:text-gray-400">
                  <tr>
                    <th className="px-3 py-2 font-medium">Path</th>
                    <th className="px-3 py-2 font-medium text-right">Views</th>
                    <th className="px-3 py-2 font-medium text-right">Sessions</th>
                    <th className="px-3 py-2 font-medium text-right">Share</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {stats.topPages.map((page) => (
                    <tr
                      key={page.path}
                      className="text-gray-700 dark:text-gray-300"
                      title={page.path}
                    >
                      <td className="max-w-[280px] truncate px-3 py-2 font-medium text-gray-800 dark:text-white/90">
                        {truncatePath(page.path)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatNumber(page.views)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatNumber(page.uniqueSessions)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-500">
                        {formatPercent(page.views, stats.totalPageViews)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-500">No page view data yet</p>
          )}
        </Panel>
      </div>

      {stats.pageEngagement.length > 0 && (
        <Panel
          title="Time on page"
          subtitle="Average duration and scroll depth when visitors leave a page"
        >
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-xs">
              <thead className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500 dark:bg-gray-900/60 dark:text-gray-400">
                <tr>
                  <th className="px-3 py-2 font-medium">Path</th>
                  <th className="px-3 py-2 font-medium text-right">Avg time</th>
                  <th className="px-3 py-2 font-medium text-right">Avg scroll</th>
                  <th className="px-3 py-2 font-medium text-right">Samples</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {stats.pageEngagement.map((row) => (
                  <tr
                    key={row.path}
                    className="text-gray-700 dark:text-gray-300"
                    title={row.path}
                  >
                    <td className="max-w-[280px] truncate px-3 py-2 font-medium text-gray-800 dark:text-white/90">
                      {truncatePath(row.path)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatDuration(row.avgDurationSec)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {row.avgScrollDepth > 0 ? `${row.avgScrollDepth}%` : "—"}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-500">
                      {formatNumber(row.samples)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
    </div>
  );
}
